/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { AlertDetailResponse } from '@/types/alert';

/**
 * 获取对话预警详情
 */
export async function fetchAlertDetail(conversationId: string): Promise<AlertDetailResponse> {
  try {
    const response = await fetch(`/api/conversations/${conversationId}/alerts/detail`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch alert detail:', error);
    return getMockAlertDetail();
  }
}

/**
 * Mock 预警详情数据
 */
export function getMockAlertDetail(): AlertDetailResponse {
  return {
    conversation_id: 'c_001',
    subject_name: '奶龙',
    overall: {
      severity: 'high',
      score_10: 8.5,
      detected_at: '2026-01-13T13:20:00Z',
      types_hit: [1, 4, 7],
    },
    reasons: ['学习压力太大', '和家长关系不融洽', '感到不被理解', '情绪低落持续时间较长'],
    related_turns: [
      {
        turn_id: 101,
        role: 'user',
        text: '我今天很难受，学习压力太大了',
        redacted: false,
        hit_types: [4, 2],
        timestamp: '2026-01-13T13:18:10Z',
      },
      {
        turn_id: 102,
        role: 'assistant',
        text: '我能理解你现在的感受。学习压力确实会让人感到难受，能跟我说说具体是哪方面让你感到压力大吗？',
        redacted: false,
        hit_types: [],
        timestamp: '2026-01-13T13:18:30Z',
      },
      {
        turn_id: 103,
        role: 'user',
        text: '妈妈也不理解我，只会骂我笨，说我什么都做不好',
        redacted: false,
        hit_types: [7, 4],
        timestamp: '2026-01-13T13:19:40Z',
      },
      {
        turn_id: 104,
        role: 'assistant',
        text: '听起来你感到很委屈和难过。当家人不理解自己的时候，确实会让人感到孤单和无助。',
        redacted: false,
        hit_types: [],
        timestamp: '2026-01-13T13:19:55Z',
      },
      {
        turn_id: 105,
        role: 'user',
        text: '我觉得自己真的很没用，做什么都做不好，有时候真的不想活了...',
        redacted: false,
        hit_types: [1, 4, 7],
        timestamp: '2026-01-13T13:20:15Z',
      },
    ],
    suggestions: [
      {
        id: 's1',
        title: '倾听接纳',
        text: '先确认对方感受，避免否定或说教，让对方感到被理解。使用共情语言，比如"我能理解你现在的感受"、"听起来你确实很辛苦"等。',
        when_to_use: '情绪异常/家庭情感类',
      },
      {
        id: 's2',
        title: '帮助识别情绪与需求',
        text: '引导对方说出最困扰的点：是压力、误解、冲突还是无助？一起拆解为可执行的小步骤，让对方感到问题是可以解决的。',
        when_to_use: '情绪异常/社交困扰',
      },
      {
        id: 's3',
        title: '必要时寻求专业帮助',
        text: '若出现明显安全风险（如自伤、自杀倾向）或持续强烈痛苦，建议立即联系可信任的人（家人、老师、朋友）或专业支持渠道（心理热线、危机干预）。',
        when_to_use: '健康人身安全相关',
      },
      {
        id: 's4',
        title: '引导积极应对',
        text: '帮助对方找到自己的优势和资源，鼓励采取小的积极行动。避免过度承诺或给出不切实际的建议。',
        when_to_use: '情绪异常/自我认知相关',
      },
      {
        id: 's5',
        title: '保持沟通开放',
        text: '表达关心和支持，让对方知道随时可以倾诉。建立安全的对话空间，不要急于给出解决方案。',
        when_to_use: '所有类型',
      },
    ],
    meta: {
      confidence: 0.82,
      model_version: 'v2.1',
      last_updated_at: '2026-01-13T13:20:05Z',
    },
  };
}
